/* global document */
import { useRef, useEffect } from 'react';

import isScrollbarClick from '../isScrollbarClick/index.ts';
import useEventCallback from '../useEventCallback/index.ts';

export type MouseEventType =
  | 'click'
  | 'dblclick'
  | 'contextmenu'
  | 'mousedown'
  | 'mouseenter'
  | 'mouseleave'
  | 'mousemove'
  | 'mouseout'
  | 'mouseover'
  | 'mouseup';

export interface UseClickOutsideOptions {
  /**
   * Whether clicking outside is actively listening (for performance)
   */
  active?: boolean;
  /**
   * Exclude clicks upon the scrollbar from being registered as a click
   */
  excludeScrollbar?: boolean;
  /**
   * ClassName(s) that won't register as a click outside
   */
  ignoreClassName?: string | string[];
  /**
   * Event types to listen to on an element
   */
  eventTypes?: MouseEventType[];
  /**
   * Whether to `stopPropagation` when handling the event bubbling
   */
  stopPropagation?: boolean;
  /**
   * Whether to `preventDefault` when handling the event bubbling
   */
  preventDefault?: boolean;
}

interface isClickedOutsideOptions extends UseClickOutsideOptions {
  ignoreClassName: string | string[];
}

function findHighest(
  current: Element,
  componentNode: Element,
  ignoreClass: string | string[]
): true | Document {
  const classesToIgnore = new Array<string>().concat(ignoreClass);
  /*
   * Adapted from: https://github.com/Pomax/react-onclickoutside/blob/master/src/index.js
   */
  if (
    current === componentNode ||
    (current?.classList && classesToIgnore.some(c => current.classList.contains(c)))
  ) {
    return true;
  }

  if (current.parentNode) {
    return findHighest(current.parentNode as Element, componentNode, ignoreClass);
  }

  return current as unknown as Document;
}

function isClickedOutside(
  e: MouseEvent,
  refNode: Element,
  { excludeScrollbar, ignoreClassName, preventDefault, stopPropagation }: isClickedOutsideOptions
): boolean {
  if (preventDefault) {
    e.preventDefault();
  }

  if (stopPropagation) {
    e.stopPropagation();
  }

  if (excludeScrollbar && isScrollbarClick(e)) {
    return false;
  }

  if (findHighest(e.target as Element, refNode, ignoreClassName) !== document) {
    return false;
  }

  return true;
}
const DEFAULT_EVENT_TYPES: MouseEventType[] = ['mousedown'];
const DEFAULT_IGNORE_CLASSNAMES = ['aglmd-ignore-click-outside', 'ignore-react-onclickoutside'];

/**
 * A hook that detects whether an element was clicked outside of.
 * If so it will call the provided handler function
 *
 * @param {Function} onClickOutsideFn The function to call when clicked outside
 * @param {Object} options options to pass to the hook
 * @param {boolean} options.active Whether clicking outside is actively listening (for performance)
 * @param {boolean} options.excludeScrollbar Whether clicking outside is excluding clicks on the scrollbar
 * @param {string | string[]} options.ignoreClassName ClassName(s) that won't register as a click outside
 * @param {MouseEventType[]} options.eventTypes Event types to listen to on an element
 * @param {boolean} options.stopPropagation Whether to `stopPropagation` when handling the event bubbling
 * @param {boolean} options.preventDefault Whether to `preventDefault` when handling the event bubbling
 * @returns {React.MutableRefObject<Element | null>} nodeRef The DOM node to listen to for clicks outside of
 * @example <caption>Example Usage</caption>
 * const ref = useClickOutside(() => {}, {
 *  active: isActive,
 *  ignoreClassName: 'ignore-class',
 *  excludeScrollbar: true
 * });
 * <div ref={ref}>Click Outside</div>
 */
export default function useClickOutside<T extends HTMLElement>(
  onClickOutsideFn: (e: MouseEvent) => void,
  {
    active = true,
    excludeScrollbar = false,
    ignoreClassName = DEFAULT_IGNORE_CLASSNAMES,
    eventTypes = DEFAULT_EVENT_TYPES,
    stopPropagation = false,
    preventDefault = false
  }: UseClickOutsideOptions = {}
): React.MutableRefObject<T | null> {
  const handleClickOutside = useEventCallback((e: MouseEvent) => {
    if (
      node.current !== null &&
      isClickedOutside(e, node.current, {
        excludeScrollbar,
        ignoreClassName,
        stopPropagation,
        preventDefault
      })
    ) {
      onClickOutsideFn(e);
    }
  });
  const node = useRef<T | null>(null);

  useEffect(() => {
    const events = ([] as MouseEventType[]).concat(eventTypes);

    if (active) {
      events.forEach(event => {
        document.addEventListener(event, handleClickOutside);
      });
    } else {
      events.forEach(event => {
        document.removeEventListener(event, handleClickOutside);
      });
    }

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleClickOutside);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    eventTypes,
    excludeScrollbar,
    // handleClickOutside,
    ignoreClassName,
    preventDefault,
    stopPropagation
  ]);

  return node;
}
